/**
 * Birthdays & anniversaries — the whole list, soonest first.
 *
 * Everyone in the house reads it; only adults get the add / edit / remove controls.
 * That split is a courtesy — worker/routes/birthdays.ts enforces it for real, since
 * this router isn't behind the adult gate.
 *
 * The page never invents state: a failed read says so and offers a retry, an empty
 * list invites the first date, and a failed save (in the form) hands the words back.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useIdentity } from '../../lib/identity';
import type { SpecialDate } from '../../shared/types';
import BirthdayForm from './BirthdayForm';
import {
  Banner,
  ErrorState,
  KindIcon,
  LoadingRows,
  KIND_META,
  btnPrimary,
  btnQuiet,
  cx,
  daysAwayLabel,
  explainError,
  formatDate,
  turningLabel,
} from './shared';

type FormState = { open: false } | { open: true; date: SpecialDate | null };

export default function BirthdaysPage() {
  const { role } = useIdentity();
  const isAdult = role === 'adult';

  const [dates, setDates] = useState<SpecialDate[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({ open: false });
  const [banner, setBanner] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await api.get<SpecialDate[]>('/birthdays');
      setDates(rows);
    } catch (err) {
      setError(explainError(err, 'We could not load your dates. Try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onSaved = useCallback(
    async (message: string) => {
      setBanner(message);
      await load();
    },
    [load]
  );

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-6 sm:px-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink">Birthdays &amp; dates</h1>
          <p className="mt-1 text-sm leading-relaxed text-ink-soft">
            Every birthday and anniversary the house keeps track of, soonest first.
          </p>
        </div>
        {isAdult && (
          <button
            type="button"
            onClick={() => setForm({ open: true, date: null })}
            className={cx(btnPrimary, 'shrink-0')}
          >
            <span aria-hidden="true" className="text-lg leading-none">
              +
            </span>
            Add
          </button>
        )}
      </header>

      {banner && <Banner tone="ok">{banner}</Banner>}

      <section className="rounded-card border border-border bg-surface p-5 sm:p-6">
        <h2 className="sr-only">Upcoming dates</h2>

        {loading && <LoadingRows rows={4} />}

        {/* A failed read is never rendered as an all-clear, empty list. */}
        {!loading && error && <ErrorState message={error} onRetry={load} />}

        {!loading && !error && dates && dates.length === 0 && (
          <div className="py-6 text-center">
            <p className="font-display text-xl font-semibold text-ink">No dates yet.</p>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-ink-soft">
              {isAdult
                ? 'Add the first birthday or anniversary and the house will start reminding everyone — two weeks out to get a gift, and again the day of.'
                : 'When a grown-up adds a birthday or anniversary, it will show up here.'}
            </p>
            {isAdult && (
              <button
                type="button"
                onClick={() => setForm({ open: true, date: null })}
                className={cx(btnPrimary, 'mt-4')}
              >
                <span aria-hidden="true" className="text-lg leading-none">
                  +
                </span>
                Add the first one
              </button>
            )}
          </div>
        )}

        {!loading && !error && dates && dates.length > 0 && (
          <ul className="divide-y divide-border">
            {dates.map((d) => (
              <DateRow
                key={d.id}
                date={d}
                isAdult={isAdult}
                onEdit={() => setForm({ open: true, date: d })}
              />
            ))}
          </ul>
        )}
      </section>

      {form.open && (
        <BirthdayForm
          date={form.date}
          onClose={() => setForm({ open: false })}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function DateRow({
  date,
  isAdult,
  onEdit,
}: {
  date: SpecialDate;
  isAdult: boolean;
  onEdit: () => void;
}) {
  const turning = turningLabel(date);
  const soon = date.days_away <= 3;

  return (
    <li className="flex items-center gap-3 py-4 first:pt-0 last:pb-0">
      <KindIcon kind={date.kind} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <p className="font-medium text-ink">{date.name}</p>
          <span className="text-xs text-ink-faint">{KIND_META[date.kind].label}</span>
        </div>
        <p className="text-sm text-ink-soft">
          <span className={cx('font-medium', soon ? 'text-clay' : 'text-ink')}>
            {daysAwayLabel(date.days_away)}
          </span>
          {' · '}
          {formatDate(date.next_at)}
          {turning && ` · ${turning}`}
        </p>
        {date.notes && <p className="mt-1 text-sm text-ink-faint">{date.notes}</p>}
      </div>
      {isAdult && (
        <button type="button" onClick={onEdit} className={cx(btnQuiet, 'shrink-0')}>
          Edit
          <span className="sr-only"> {date.name}</span>
        </button>
      )}
    </li>
  );
}
